import { Button, TextAreaField, TextField } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useMemo, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { TbArrowBackUp, TbX } from 'react-icons/tb';
import { toast } from 'react-toastify';
import LoadingBars from '../../custom-components/LoadingBars';
import { usePermission } from '../../hooks/usePermission';
import { applyBeaconIds, parseIds } from './beacon-ids';
import { toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { useNavigatorStore } from '../../zustand/useNavigatorStore';
import { BEACON_COLOR_ROWS, beaconColorCss } from './beacon-colors';

/** A count with its noun: "1 ID", "2 IDs", "1,200 institutions". */
const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

interface BeaconDetailProps {
  beacon: Edulead.EduleadV4Beacon;
  /** Whether the form is in view, not sliding out: only then do its member rows edit. */
  isOpen: boolean;
  /** Whether the beacon's members are still being read; the count and the map wait for them. */
  isLoadingMembers: boolean;
  onBack: () => void;
  /** Called with the beacon a save returned. */
  onChanged: (beacon: Edulead.EduleadV4Beacon) => void;
}

/**
 * One beacon's form: its name, how many institutions it holds, a Bulk add that opens a paste of
 * ids, and the edits made on the map since it was selected or last saved, with Save and Discard.
 * The map's edits reach the API on Save; a bulk add goes straight to it, as the ids may be NCES
 * ids the map cannot stage, so it waits for the map's edits to be saved or discarded.
 */
export function BeaconDetail({
  beacon,
  isOpen,
  isLoadingMembers,
  onBack,
  onChanged,
}: BeaconDetailProps) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission('beacons:manage');
  const [name, setName] = useState(beacon.name ?? '');
  const [isSaving, setIsSaving] = useState(false);
  // The color being saved, while one is.
  const [isSavingColor, setIsSavingColor] = useState(false);
  // The bulk add: whether its paste is open, the ids pasted, and whether the import is under way.
  const [isPasteOpen, setIsPasteOpen] = useState(false);
  const [paste, setPaste] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const editingBeaconId = useNavigatorStore((state) => state.editingBeaconId);
  const edited = useNavigatorStore((state) => state.highlightedInstitutionIds);
  const saved = useNavigatorStore((state) => state.beaconSavedIds);
  const isSavingEdits = useNavigatorStore((state) => state.isSavingBeacon);
  const paint = useNavigatorStore((state) => state.paintBeacon);
  const names = useNavigatorStore((state) => state.institutionNames);
  const setSavedIds = useNavigatorStore((state) => state.setBeaconSavedIds);
  const setHighlighted = useNavigatorStore((state) => state.setHighlightedInstitutionIds);
  const rememberNames = useNavigatorStore((state) => state.rememberInstitutionNames);

  // The members' buttons edit while the map edits this beacon (the panel's choice: it is the one
  // selected and the user may change it) and the form is in view.
  const canEdit = isOpen && editingBeaconId === beacon.id;

  // The members by name, saved and edited together: each kept, added since the saved set, or
  // removed from it (still listed, struck through, until saved or restored).
  const rows = useMemo(() => {
    if (!saved) return [];
    const savedSet = new Set(saved);
    const editedSet = new Set(edited);
    return [...new Set([...edited, ...saved])]
      .map((id) => ({
        id,
        name: names[id] ?? 'Unknown institution',
        status: !savedSet.has(id) ? 'added' : !editedSet.has(id) ? 'removed' : 'kept',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [edited, saved, names]);
  const added = rows.filter((r) => r.status === 'added').length;
  const removed = rows.filter((r) => r.status === 'removed').length;
  const isDirty = added > 0 || removed > 0;

  // The import's ids, and whether it may run: the map edits this beacon, holds nothing unsaved,
  // and no save or import is under way.
  const importIds = parseIds(paste);
  const canImport = canEdit && saved !== null && !isDirty && !isSavingEdits && !isImporting;

  /**
   * Adds the pasted ids to the beacon, batch by batch, and puts what the API now holds in the
   * store: the panel takes the count and its cache from there, the map the highlight.
   */
  const importPaste = async () => {
    if (!beacon.id || !canImport || importIds.length === 0) return;
    setIsImporting(true);
    // The map's strokes and the toolbar hold while the import runs, as they do for a save.
    useNavigatorStore.setState({ isSavingBeacon: true });
    try {
      const { items, unmatchedIds } = await applyBeaconIds(beacon.id, importIds, 'Added');
      const before = new Set(useNavigatorStore.getState().beaconSavedIds ?? []);
      const fresh = [...new Set(items.flatMap((m) => (m.id && !before.has(m.id) ? [m.id] : [])))];
      const named: Record<string, string> = {};
      items.forEach((m) => {
        if (m.id && m.name) named[m.id] = m.name;
      });
      if (Object.keys(named).length > 0) rememberNames(named);
      const now = [...before, ...fresh];
      setSavedIds(now);
      setHighlighted(now);
      setPaste('');
      setIsPasteOpen(false);
      const already = items.length - fresh.length;
      toast.success(
        [
          `Added ${plural(fresh.length, 'institution')}`,
          already > 0 ? `${already.toLocaleString()} already in the beacon` : '',
          unmatchedIds.length > 0 ? `${plural(unmatchedIds.length, 'ID')} matched nothing` : '',
        ]
          .filter((part) => part !== '')
          .join('; '),
      );
    } catch (error) {
      toastV4Error(error, 'Failed to add institutions');
    } finally {
      useNavigatorStore.setState({ isSavingBeacon: false });
      setIsImporting(false);
    }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setName(beacon.name ?? '');
      return;
    }
    if (trimmed === beacon.name || !beacon.id) return;
    try {
      setIsSaving(true);
      const res = await eduleadV4.beacons.update({
        id: beacon.id,
        update_mask: 'name',
        body: { name: trimmed },
      });
      if (res.beacon) onChanged(res.beacon);
    } catch (error) {
      toastV4Error(error, 'Failed to rename beacon');
      setName(beacon.name ?? '');
    } finally {
      setIsSaving(false);
    }
  };

  /** Saves a color ("" for the default) and hands the returned beacon up: the list and the map follow. */
  const saveColor = async (color: string) => {
    if (!beacon.id || color === (beacon.color ?? '') || isSavingColor) return;
    try {
      setIsSavingColor(true);
      const res = await eduleadV4.beacons.update({
        id: beacon.id,
        update_mask: 'color',
        body: { color },
      });
      if (res.beacon) onChanged(res.beacon);
    } catch (error) {
      toastV4Error(error, 'Failed to recolor beacon');
    } finally {
      setIsSavingColor(false);
    }
  };

  const swatch = (color: string, title: string) => (
    <button
      key={color || 'default'}
      type="button"
      className={`beacon-detail__swatch ${(beacon.color ?? '') === color ? 'beacon-detail__swatch--selected' : ''}`}
      style={{ backgroundColor: beaconColorCss(color) }}
      title={title}
      aria-label={title}
      aria-pressed={(beacon.color ?? '') === color}
      disabled={!canManage || isSavingColor}
      onClick={() => saveColor(color)}
    />
  );

  return (
    <div className="beacon-detail">
      <button
        type="button"
        className="navigator-beacons-panel__bar beacon-detail__back"
        onClick={onBack}
      >
        <FaArrowLeft size={11} /> Beacons
      </button>

      {/* Between the bar and the actions: what scrolls when the members run long */}
      <div className="beacon-detail__body">
        <div className="beacon-detail__section">
          <div className="beacon-detail__section-title">Name</div>
          <TextField
            label="Name"
            labelHidden
            size="small"
            value={name}
            isDisabled={!canManage || isSaving}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
          />
        </div>

        <div className="beacon-detail__section">
          <div className="beacon-detail__section-title">Color</div>
          {/* The palette's hues, a row per shade; a beacon without one is the map's orange */}
          <div className="beacon-detail__swatches">
            {BEACON_COLOR_ROWS.map((row) => (
              <div key={row[0]} className="beacon-detail__swatch-row">
                {row.map((color) => swatch(color, color.replace('color:', '').replace('-', ' ')))}
              </div>
            ))}
          </div>
        </div>

        <div className="beacon-detail__section beacon-detail__section--members">
          <div className="beacon-detail__section-title beacon-detail__count">
            Institutions{saved !== null && ` (${edited.length.toLocaleString()})`}
            {isDirty && (
              <span className="beacon-detail__diff">
                {added > 0 && <span className="beacon-detail__diff--added">+{added.toLocaleString()}</span>}
                {removed > 0 && (
                  <span className="beacon-detail__diff--removed">−{removed.toLocaleString()}</span>
                )}
              </span>
            )}
            {canManage && (
              // Always in the row, so the title keeps its height: it opens the paste and closes it.
              <Button
                size="small"
                className="silent tiny beacon-detail__bulk-add"
                isDisabled={saved === null || isImporting}
                onClick={() => {
                  if (isPasteOpen) setPaste('');
                  setIsPasteOpen(!isPasteOpen);
                }}
              >
                {isPasteOpen ? 'Cancel' : 'Bulk add'}
              </Button>
            )}
          </div>
          {canManage && isPasteOpen && (
            <div className="beacon-detail__import">
              <TextAreaField
                label="Institution IDs"
                labelHidden
                size="small"
                rows={8}
                resize="vertical"
                placeholder="NCES or Edulead IDs, one per line or comma separated"
                value={paste}
                isDisabled={isImporting}
                autoFocus
                onChange={(e) => setPaste(e.target.value)}
              />
              <div className="beacon-detail__import-actions">
                <Button
                  size="small"
                  variation="primary"
                  className="tiny"
                  isDisabled={!canImport || importIds.length === 0}
                  isLoading={isImporting}
                  loadingText="Adding…"
                  onClick={importPaste}
                >
                  {importIds.length > 0 ? `Add ${plural(importIds.length, 'ID')}` : 'Add'}
                </Button>
              </div>
              {isDirty && importIds.length > 0 && (
                <div className="beacon-detail__import-hint">
                  Save or discard the map's edits first.
                </div>
              )}
            </div>
          )}
          {isLoadingMembers || saved === null ? (
            <LoadingBars numberOfRows={1} />
          ) : (
            <>
              {rows.length > 0 && (
                <div className="beacon-detail__list">
                  {rows.map((row) => (
                    <div key={row.id} className={`beacon-detail__row beacon-detail__row--${row.status}`}>
                      <span className="beacon-detail__row-name" title={row.name}>
                        {row.name}
                      </span>
                      {canEdit && !isSavingEdits && (
                        <Button
                          size="small"
                          className="nohover tiny beacon-detail__row-action"
                          title={row.status === 'removed' ? 'Keep in the beacon' : 'Remove from the beacon'}
                          onClick={() => paint([row.id], row.status === 'removed' ? 'Added' : 'Removed')}
                        >
                          {row.status === 'removed' ? <TbArrowBackUp /> : <TbX />}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
